import React, { Component } from "react";
import { Title } from "react-admin";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import COLORS from "../../theme/colors";
import { monterColor } from "../../theme/monterColors";

// The card colour shows the status, the thick left edge shows the monter.
const STATUS_STYLES = {
  IS_ACTIVE: { background: COLORS.primaryLight, text: COLORS.primary },
  IS_DONE: { background: COLORS.successLight, text: COLORS.success },
  IS_CANCELED: { background: COLORS.neutralLight, text: COLORS.neutral }
};
const statusStyle = status => STATUS_STYLES[status] || STATUS_STYLES.IS_ACTIVE;
const STATUS_LABELS = {
  IS_ACTIVE: "Odprto",
  IS_DONE: "Končano",
  IS_CANCELED: "Preklicano"
};

const DAY_OPTIONS = [1, 3, 5, 7];
const DAY_NAMES = ["ned", "pon", "tor", "sre", "čet", "pet", "sob"];
const MONTH_NAMES = [
  "januar", "februar", "marec", "april", "maj", "junij",
  "julij", "avgust", "september", "oktober", "november", "december"
];

const HOUR_HEIGHT = 52;
const DEFAULT_FIRST_HOUR = 6;
const DEFAULT_LAST_HOUR = 20;
// orders only have a start time; the app shows them as 1.5 hours long
const DEFAULT_DURATION_MINUTES = 90;
const MIN_VIEW_MINUTES = 60;
const DAYS_STORAGE_KEY = "ordersCalendarDays";
// clicking an empty spot creates an order starting at that half hour, one hour long
const SLOT_MINUTES = 30;
const NEW_ORDER_MINUTES = 60;

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
const minutesOfDay = date => date.getHours() * 60 + date.getMinutes();
const pad = n => (n < 10 ? `0${n}` : `${n}`);
const formatTime = date => `${date.getHours()}:${pad(date.getMinutes())}`;

const formatRange = (from, days) => {
  const to = addDays(from, days - 1);
  if (days === 1) {
    return `${DAY_NAMES[from.getDay()]}, ${from.getDate()}. ${MONTH_NAMES[from.getMonth()]} ${from.getFullYear()}`;
  }
  if (from.getMonth() === to.getMonth()) {
    return `${from.getDate()}.–${to.getDate()}. ${MONTH_NAMES[to.getMonth()]} ${to.getFullYear()}`;
  }
  const fromYear = from.getFullYear() !== to.getFullYear() ? ` ${from.getFullYear()}` : "";
  return `${from.getDate()}. ${MONTH_NAMES[from.getMonth()]}${fromYear} – ${to.getDate()}. ${MONTH_NAMES[to.getMonth()]} ${to.getFullYear()}`;
};

// Slovenian plural: 1 nalog, 2 naloga, 3-4 nalogi, 5+ nalogov
const ordersCount = n => {
  const rest = n % 100;
  if (rest === 1) return `${n} nalog`;
  if (rest === 2) return `${n} naloga`;
  if (rest === 3 || rest === 4) return `${n} nalogi`;
  return `${n} nalogov`;
};

const customerName = customer => {
  if (!customer) return "Brez stranke";
  if (customer.companyName) return customer.companyName;
  return [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Brez stranke";
};

const toEvent = order => {
  const start = new Date(order.startInstallationDate);
  if (isNaN(start.getTime())) return null;

  let end = order.endInstallationDate ? new Date(order.endInstallationDate) : null;
  if (!end || isNaN(end.getTime()) || end <= start || end - start > 24 * 3600000) {
    end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60000);
  }

  // Where the card is drawn: always inside the start day, so a late order (e.g. 23:30 – 1:00)
  // still shows up at the bottom of its day instead of running off the grid. The label keeps the real times.
  const dayEnd = addDays(start, 1);
  const viewStart = new Date(Math.min(start, dayEnd - MIN_VIEW_MINUTES * 60000));
  const viewEnd = new Date(Math.max(Math.min(end, dayEnd), viewStart.getTime() + MIN_VIEW_MINUTES * 60000));

  const monter = order.appUser;
  const color = monterColor(monter);

  return {
    id: order.id,
    start,
    end,
    viewStart,
    viewEnd,
    status: order.status,
    customer: customerName(order.clientCustomer),
    city: order.clientCustomer ? order.clientCustomer.city : null,
    monter: monter ? [monter.firstName, monter.lastName].filter(Boolean).join(" ") : null,
    monterId: monter ? monter.id : null,
    orderNumber: order.orderNumber,
    monterColor: color
  };
};

/** Puts overlapping events side by side, like a normal calendar. */
const layoutDay = events => {
  const sorted = events.slice().sort((a, b) => a.viewStart - b.viewStart || b.viewEnd - a.viewEnd);
  const laidOut = [];
  let cluster = [];
  let lanes = [];
  let clusterEnd = 0;

  const closeCluster = () => {
    cluster.forEach(item => {
      item.lanes = lanes.length;
    });
    cluster = [];
    lanes = [];
  };

  sorted.forEach(event => {
    if (cluster.length && event.viewStart >= clusterEnd) {
      closeCluster();
    }
    let lane = lanes.findIndex(laneEnd => laneEnd <= event.viewStart);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(event.viewEnd);
    } else {
      lanes[lane] = event.viewEnd;
    }
    const item = { ...event, lane };
    cluster.push(item);
    laidOut.push(item);
    clusterEnd = cluster.length === 1 ? event.viewEnd : new Date(Math.max(clusterEnd, event.viewEnd));
  });
  closeCluster();
  return laidOut;
};

const styles = {
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: `1px solid ${COLORS.border}`
  },
  navigation: { display: "flex", alignItems: "center" },
  actions: { display: "flex", alignItems: "center" },
  updated: { marginRight: 12, color: COLORS.muted, fontSize: 13 },
  refreshError: {
    padding: "8px 20px",
    backgroundColor: "#fdecea",
    color: COLORS.error,
    fontSize: 13
  },
  range: { marginLeft: 12, color: COLORS.text, fontSize: 18, fontWeight: 700 },
  segmented: {
    display: "inline-flex",
    padding: 3,
    borderRadius: 10,
    backgroundColor: COLORS.field,
    border: `1px solid ${COLORS.border}`
  },
  segment: {
    minWidth: 0,
    minHeight: 30,
    padding: "4px 12px",
    borderRadius: 8,
    color: COLORS.muted,
    fontSize: 13
  },
  segmentActive: {
    backgroundColor: "#fff",
    color: COLORS.primary,
    boxShadow: "0 1px 3px rgba(10, 40, 70, 0.15)"
  },
  scroller: {
    maxHeight: "calc(100vh - 300px)",
    minHeight: 360,
    overflow: "auto"
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    display: "flex",
    backgroundColor: "#fff",
    borderBottom: `1px solid ${COLORS.border}`
  },
  gutter: { flexShrink: 0, width: 56 },
  dayHeader: {
    flex: 1,
    minWidth: 120,
    padding: "10px 0",
    borderLeft: `1px solid ${COLORS.grid}`,
    textAlign: "center"
  },
  dayName: { color: COLORS.muted, fontSize: 12, fontWeight: 600, textTransform: "uppercase" },
  dayNumber: {
    display: "inline-block",
    minWidth: 32,
    height: 32,
    marginTop: 2,
    borderRadius: 16,
    color: COLORS.text,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "32px"
  },
  dayNumberToday: { backgroundColor: COLORS.primary, color: "#fff" },
  body: { display: "flex", position: "relative" },
  hourLabel: {
    position: "absolute",
    right: 8,
    color: COLORS.muted,
    fontSize: 11,
    transform: "translateY(-50%)"
  },
  dayColumn: {
    position: "relative",
    flex: 1,
    minWidth: 120,
    cursor: "pointer",
    borderLeft: `1px solid ${COLORS.grid}`,
    backgroundImage: `linear-gradient(to bottom, ${COLORS.grid} 1px, transparent 1px)`,
    backgroundSize: `100% ${HOUR_HEIGHT}px`
  },
  dayColumnToday: { backgroundColor: "rgba(0, 131, 213, 0.03)" },
  slot: {
    position: "absolute",
    left: 3,
    right: 3,
    boxSizing: "border-box",
    padding: "4px 8px",
    border: `1.5px dashed ${COLORS.primary}`,
    borderRadius: 6,
    backgroundColor: "rgba(0, 131, 213, 0.06)",
    color: COLORS.primary,
    fontSize: 12,
    fontWeight: 700,
    pointerEvents: "none"
  },
  event: {
    position: "absolute",
    boxSizing: "border-box",
    overflow: "hidden",
    padding: "4px 6px 4px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 12,
    lineHeight: "15px",
    transition: "box-shadow 0.15s"
  },
  eventTime: { color: COLORS.muted, fontSize: 11, fontWeight: 600 },
  eventCustomer: { color: COLORS.text, fontWeight: 700 },
  eventDetail: { color: COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  eventStatus: { marginTop: 2, fontSize: 10, fontWeight: 700, textTransform: "uppercase" },
  now: {
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 2,
    height: 2,
    backgroundColor: COLORS.error
  },
  footer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    borderTop: `1px solid ${COLORS.border}`,
    color: COLORS.muted,
    fontSize: 13
  },
  legend: { display: "flex", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", marginRight: 16 },
  legendTitle: { marginRight: 10, color: COLORS.text, fontWeight: 600 },
  legendSwatch: {
    width: 14,
    height: 12,
    marginRight: 6,
    boxSizing: "border-box",
    border: "1px solid",
    borderRadius: 3
  },
  legendBar: { width: 4, height: 14, marginRight: 6, borderRadius: 2 },
  message: { padding: 40, color: COLORS.muted, textAlign: "center" }
};

export default class OrderCalendar extends Component {
  constructor(props) {
    super(props);
    const stored = parseInt(localStorage.getItem(DAYS_STORAGE_KEY), 10);
    const days = DAY_OPTIONS.indexOf(stored) !== -1 ? stored : 7;
    this.state = {
      days,
      from: startOfDay(new Date()),
      orders: [],
      loading: true,
      error: null,
      refreshing: false,
      refreshError: null,
      updatedAt: null,
      hovered: null,
      slot: null
    };
    this.scroller = React.createRef();
  }

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => this.forceUpdate(), 60000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  /** First load scrolls to the morning; a refresh keeps the current view and scroll position. */
  load = (refresh = false) => {
    if (refresh) {
      this.setState({ refreshing: true, refreshError: null });
    }
    fetch("/api/orders/list", {
      headers: { Authorization: localStorage.getItem("token"), Accept: "application/json" }
    })
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(orders => {
        const firstLoad = this.state.loading || this.state.error;
        this.setState(
          {
            orders: Array.isArray(orders) ? orders : [],
            loading: false,
            refreshing: false,
            error: null,
            updatedAt: new Date()
          },
          firstLoad ? this.scrollToMorning : undefined
        );
      })
      .catch(() =>
        this.setState(({ orders }) =>
          refresh && orders.length
            ? { refreshing: false, refreshError: "Osvežitev ni uspela, prikazani so prejšnji podatki." }
            : { loading: false, refreshing: false, error: "Naročil ni bilo mogoče naložiti." }
        )
      );
  };

  scrollToMorning = () => {
    if (this.scroller.current) {
      this.scroller.current.scrollTop = Math.max(0, (7 - this.firstHour()) * HOUR_HEIGHT - 8);
    }
  };

  setDays = days => {
    localStorage.setItem(DAYS_STORAGE_KEY, days);
    this.setState({ days });
  };

  move = direction => this.setState(({ from, days }) => ({ from: addDays(from, direction * days) }));

  goToday = () =>
    this.setState({ from: startOfDay(new Date()) });

  events() {
    return this.state.orders
      .filter(order => order.startInstallationDate)
      .map(toEvent)
      .filter(Boolean);
  }

  visibleDays() {
    const { from, days } = this.state;
    return Array.from({ length: days }, (_, i) => addDays(from, i));
  }

  firstHour() {
    const days = this.visibleDays();
    const hours = this.events()
      .filter(e => days.some(d => sameDay(d, e.start)))
      .map(e => e.viewStart.getHours());
    return Math.min(DEFAULT_FIRST_HOUR, ...hours);
  }

  lastHour() {
    const days = this.visibleDays();
    const hours = this.events()
      .filter(e => days.some(d => sameDay(d, e.start)))
      .map(e => Math.ceil((e.viewEnd - startOfDay(e.start)) / 3600000));
    return Math.min(24, Math.max(DEFAULT_LAST_HOUR, ...hours));
  }

  openOrder = id => this.props.history.push(`/orders/${id}`);

  /** The half-hour slot under the mouse, or null when the mouse is over an order. */
  slotAt = (event, day) => {
    if (event.target !== event.currentTarget) return null;
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
    const minutes = this.firstHour() * 60 + Math.floor(((y / HOUR_HEIGHT) * 60) / SLOT_MINUTES) * SLOT_MINUTES;
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
    return { start, end: new Date(start.getTime() + NEW_ORDER_MINUTES * 60000) };
  };

  hoverSlot = (event, day) => {
    const slot = this.slotAt(event, day);
    const current = this.state.slot;
    if (!slot && !current) return;
    if (slot && current && slot.start.getTime() === current.start.getTime()) return;
    this.setState({ slot });
  };

  createOrder = (event, day) => {
    const slot = this.slotAt(event, day);
    if (!slot) return;
    this.props.history.push({
      pathname: "/orders/create",
      state: {
        record: {
          startInstallationDate: slot.start.toISOString(),
          endInstallationDate: slot.end.toISOString()
        }
      }
    });
  };

  renderSlot(firstHour) {
    const { slot } = this.state;
    const top = ((minutesOfDay(slot.start) - firstHour * 60) / 60) * HOUR_HEIGHT;
    return (
      <div style={{ ...styles.slot, top: top + 1, height: (NEW_ORDER_MINUTES / 60) * HOUR_HEIGHT - 2 }}>
        + {formatTime(slot.start)} – {formatTime(slot.end)}
      </div>
    );
  }

  renderEvent(event, firstHour) {
    const top = ((minutesOfDay(event.viewStart) - firstHour * 60) / 60) * HOUR_HEIGHT;
    const height = Math.max(((event.viewEnd - event.viewStart) / 3600000) * HOUR_HEIGHT - 2, 24);
    const width = 100 / event.lanes;
    const canceled = event.status === "IS_CANCELED";
    const hovered = this.state.hovered === event.id;

    return (
      <div
        key={event.id}
        role="button"
        tabIndex={0}
        title={`${event.orderNumber || ""} ${event.customer}`.trim()}
        onClick={e => {
          e.stopPropagation();
          this.openOrder(event.id);
        }}
        onKeyDown={e => e.key === "Enter" && this.openOrder(event.id)}
        onMouseEnter={() => this.setState({ hovered: event.id })}
        onMouseLeave={() => this.setState({ hovered: null })}
        style={{
          ...styles.event,
          top: top + 1,
          height,
          left: `calc(${event.lane * width}% + 3px)`,
          width: `calc(${width}% - 6px)`,
          zIndex: hovered ? 2 : 1,
          backgroundColor: statusStyle(event.status).background,
          borderLeft: `4px solid ${event.monterColor}`,
          boxShadow: hovered ? "0 4px 12px rgba(10, 40, 70, 0.18)" : "none"
        }}
      >
        <div style={styles.eventTime}>
          {formatTime(event.start)} – {formatTime(event.end)}
        </div>
        <div style={{ ...styles.eventCustomer, textDecoration: canceled ? "line-through" : "none" }}>
          {event.customer}
        </div>
        {event.city && <div style={styles.eventDetail}>{event.city}</div>}
        {event.monter && <div style={styles.eventDetail}>{event.monter}</div>}
        {STATUS_LABELS[event.status] && event.status !== "IS_ACTIVE" && (
          <div style={{ ...styles.eventStatus, color: statusStyle(event.status).text }}>{STATUS_LABELS[event.status]}</div>
        )}
      </div>
    );
  }

  render() {
    const { days, from, loading, error } = this.state;
    const today = new Date();
    const visibleDays = this.visibleDays();
    const events = this.events();
    const firstHour = this.firstHour();
    const lastHour = this.lastHour();
    const hours = Array.from({ length: lastHour - firstHour }, (_, i) => firstHour + i);
    const height = hours.length * HOUR_HEIGHT;

    const inRange = events.filter(e => visibleDays.some(d => sameDay(d, e.start)));
    const withoutDate = this.state.orders.filter(o => !o.startInstallationDate).length;
    const monters = {};
    inRange.forEach(e => {
      monters[e.monterId || 0] = { name: e.monter || "Brez monterja", color: e.monterColor };
    });

    return (
      <div>
        <Title title="Delovni nalogi" />
        <div style={styles.toolbar}>
          <div style={styles.navigation}>
            <Tooltip title="Nazaj">
              <IconButton aria-label="Nazaj" onClick={() => this.move(-1)}>
                <ChevronLeft />
              </IconButton>
            </Tooltip>
            <Tooltip title="Naprej">
              <IconButton aria-label="Naprej" onClick={() => this.move(1)}>
                <ChevronRight />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" color="primary" onClick={this.goToday} style={{ marginLeft: 4 }}>
              Danes
            </Button>
            <span style={styles.range}>{formatRange(from, days)}</span>
          </div>
          <div style={styles.actions}>
          {this.state.updatedAt && (
            <span style={styles.updated}>Osveženo ob {formatTime(this.state.updatedAt)}</span>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => this.load(true)}
            disabled={this.state.refreshing}
            style={{ marginRight: 12 }}
          >
            {this.state.refreshing ? (
              <CircularProgress size={18} thickness={5} style={{ marginRight: 8 }} />
            ) : (
              <RefreshIcon style={{ marginRight: 6, fontSize: 20 }} />
            )}
            Osveži
          </Button>
          <div style={styles.segmented} role="group" aria-label="Število dni">
            {DAY_OPTIONS.map(option => (
              <Button
                key={option}
                onClick={() => this.setDays(option)}
                aria-pressed={days === option}
                style={{ ...styles.segment, ...(days === option ? styles.segmentActive : {}) }}
              >
                {option === 1 ? "1 dan" : `${option} dni`}
              </Button>
            ))}
          </div>
          </div>
        </div>
        {this.state.refreshError && (
          <div style={styles.refreshError}>{this.state.refreshError}</div>
        )}

        {loading && <div style={styles.message}>Nalaganje …</div>}
        {error && <div style={{ ...styles.message, color: COLORS.error }}>{error}</div>}

        {!loading && !error && (
          <div style={styles.scroller} ref={this.scroller}>
            <div style={styles.header}>
              <div style={styles.gutter} />
              {visibleDays.map(day => {
                const isToday = sameDay(day, today);
                return (
                  <div key={day.getTime()} style={styles.dayHeader}>
                    <div style={{ ...styles.dayName, color: isToday ? COLORS.primary : COLORS.muted }}>
                      {DAY_NAMES[day.getDay()]}
                    </div>
                    <div style={{ ...styles.dayNumber, ...(isToday ? styles.dayNumberToday : {}) }}>
                      {day.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={styles.body}>
              <div style={{ ...styles.gutter, position: "relative", height }}>
                {hours.map((hour, i) =>
                  i === 0 ? null : (
                    <div key={hour} style={{ ...styles.hourLabel, top: i * HOUR_HEIGHT }}>
                      {hour}:00
                    </div>
                  )
                )}
              </div>
              {visibleDays.map(day => {
                const isToday = sameDay(day, today);
                const dayEvents = layoutDay(events.filter(e => sameDay(e.start, day)));
                const nowTop = ((minutesOfDay(today) - firstHour * 60) / 60) * HOUR_HEIGHT;
                return (
                  <div
                    key={day.getTime()}
                    title="Klikni za nov nalog"
                    onClick={e => this.createOrder(e, day)}
                    onMouseMove={e => this.hoverSlot(e, day)}
                    onMouseLeave={() => this.setState({ slot: null })}
                    style={{ ...styles.dayColumn, ...(isToday ? styles.dayColumnToday : {}), height }}
                  >
                    {this.state.slot && sameDay(this.state.slot.start, day) && this.renderSlot(firstHour)}
                    {dayEvents.map(event => this.renderEvent(event, firstHour))}
                    {isToday && nowTop >= 0 && nowTop <= height && <div style={{ ...styles.now, top: nowTop }} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && !error && (
          <div style={styles.footer}>
            <div style={styles.legend}>
              <span style={styles.legendTitle}>Status:</span>
              {Object.keys(STATUS_STYLES).map(status => (
                <span key={status} style={styles.legendItem}>
                  <span
                    style={{
                      ...styles.legendSwatch,
                      backgroundColor: STATUS_STYLES[status].background,
                      borderColor: STATUS_STYLES[status].text
                    }}
                  />
                  {STATUS_LABELS[status]}
                </span>
              ))}
              {Object.keys(monters).length > 0 && <span style={styles.legendTitle}>Monter:</span>}
              {Object.keys(monters).map(id => (
                <span key={id} style={styles.legendItem}>
                  <span style={{ ...styles.legendBar, backgroundColor: monters[id].color }} />
                  {monters[id].name}
                </span>
              ))}
              {inRange.length === 0 && <span>V tem obdobju ni nalogov.</span>}
            </div>
            {withoutDate > 0 && <span>Brez datuma montaže (ni v koledarju): {ordersCount(withoutDate)}</span>}
          </div>
        )}
      </div>
    );
  }
}
